import { useId, useLayoutEffect, useRef, useState } from 'react'
import { Button, Field, KiLabel, Textarea, type TextareaProps } from 'admination-react'
import { DictationButton } from './DictationButton'
import { useDictationKi } from './dictation'
import type { FieldHint } from './FormRenderer'

export type TextFieldInputMode = NonNullable<TextareaProps['inputMode']>

function filterChars(text: string, filter: RegExp): string {
  return Array.from(text)
    .filter((ch) => filter.test(ch))
    .join('')
}

function isAllowed(text: string, filter: RegExp): boolean {
  return filterChars(text, filter) === text
}

const DICTATION_BUSY_TEXT = "Ihr Diktat wird in Text umgewandelt …"

export function TextField({
  label,
  showLabel = true,
  value,
  onChange,
  invalid = false,
  errorMessage,
  hint,
  readOnly = false,
  required = false,
  filter,
  inputMode,
  maxLength,
  dictation = false,
  dictationContext,
}: {
  label: string
  showLabel?: boolean
  value: string
  onChange: (v: string) => void
  invalid?: boolean
  errorMessage?: string
  hint?: FieldHint
  readOnly?: boolean | 'frozen'
  required?: boolean
  filter?: RegExp
  inputMode?: TextFieldInputMode
  maxLength?: number
  dictation?: boolean
  dictationContext?: string
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const id = useId()
  const ki = useDictationKi(value, onChange)
  const [transcribing, setTranscribing] = useState(false)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const guard = (e: InputEvent) => {
      if (e.inputType === 'insertLineBreak' || e.inputType === 'insertParagraph') {
        e.preventDefault()
        return
      }
      if (filter && e.data && !isAllowed(e.data, filter)) e.preventDefault()
    }
    el.addEventListener('beforeinput', guard)
    return () => el.removeEventListener('beforeinput', guard)
  }, [filter])
  return (
    <Field
      label={showLabel ? label : undefined}
      htmlFor={id}
      required={required}
      error={errorMessage}
      hint={transcribing ? DICTATION_BUSY_TEXT : hint?.text}
      hintTone={transcribing ? 'info' : hint?.tone}
    >
      <div className={dictation ? 'text-field-wrap' : undefined}>
        <Textarea
          ref={ref}
          id={id}
          className={[dictation && 'has-dictation', readOnly === 'frozen' && 'is-frozen'].filter(Boolean).join(' ')}
          rows={1}
          value={value}
          aria-label={showLabel ? undefined : label}
          aria-invalid={invalid || undefined}
          aria-required={required || undefined}
          readOnly={!!readOnly}
          inputMode={inputMode}
          maxLength={maxLength}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault()
          }}
          onPaste={(e) => {
            if (readOnly) return
            const text = e.clipboardData.getData('text/plain')
            const flat = /[\r\n]/.test(text) ? text.replace(/[\r\n]+/g, ' ') : text
            const clean = filter ? filterChars(flat, filter) : flat
            if (clean === text) return
            e.preventDefault()
            if (!document.execCommand('insertText', false, clean)) {
              const el = e.currentTarget
              el.setRangeText(clean, el.selectionStart, el.selectionEnd, 'end')
              el.dispatchEvent(new Event('input', { bubbles: true }))
            }
          }}
          onChange={(e) => {
            let v = e.target.value.replace(/[\r\n]/g, ' ')
            if (filter) v = filterChars(v, filter)
            if (maxLength != null) v = v.slice(0, maxLength)
            onChange(v)
          }}
        />
        {dictation && !readOnly && (
          <DictationButton
            value={value}
            onChange={onChange}
            feld={label}
            kontext={dictationContext}
            onInserted={ki.onInserted}
            onBusyChange={setTranscribing}
          />
        )}
      </div>
      {dictation && !readOnly && ki.showKi && (
        <div className="field-hint is-info field-hint--row">
          <KiLabel kind="formuliert" />
          <Button type="button" variant="text" onClick={ki.confirm}>
            Übernehmen</Button>
          {ki.canUseTranskript && (
            <Button type="button" variant="text" onClick={ki.takeTranskript}>
              Originaltranskript verwenden</Button>
          )}
        </div>
      )}
    </Field>
  )
}
